import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";

const STORAGE_KEY = "johny_cookie_consent_v1";

const defaultPrefs = { essential: true, analytics: false, marketing: false };

const applyConsent = (prefs) => {
  try {
    if (window.posthog && typeof window.posthog.opt_in_capturing === "function") {
      if (prefs.analytics) window.posthog.opt_in_capturing();
      else window.posthog.opt_out_capturing();
    }
  } catch { /* noop */ }
};

export const readConsent = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch { return null; }
};

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [prefs, setPrefs] = useState(defaultPrefs);

  useEffect(() => {
    const stored = readConsent();
    if (!stored) {
      setVisible(true);
      // Default: opt out of analytics until user chooses
      applyConsent(defaultPrefs);
    } else {
      applyConsent(stored);
    }
  }, []);

  const persist = (p) => {
    const record = { ...p, essential: true, ts: new Date().toISOString() };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
    applyConsent(record);
    setVisible(false);
    setShowPrefs(false);
  };

  const acceptAll = () => persist({ essential: true, analytics: true, marketing: true });
  const rejectAll = () => persist({ essential: true, analytics: false, marketing: false });
  const savePrefs = () => persist(prefs);

  if (!visible) return null;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[70] px-4 pb-4 sm:px-6 sm:pb-6"
      role="dialog"
      aria-live="polite"
      aria-label="Consimțământ cookie-uri"
      data-testid="cookie-consent"
    >
      <div className="max-w-5xl mx-auto bg-[#0a0a0a]/95 backdrop-blur-md border border-[#D4AF37]/30 shadow-[0_20px_60px_rgba(0,0,0,0.6)]">
        {!showPrefs ? (
          <div className="p-6 sm:p-8 flex flex-col lg:flex-row lg:items-center gap-6">
            <div className="flex items-start gap-4 flex-1">
              <div className="hidden sm:flex w-11 h-11 shrink-0 items-center justify-center border border-[#D4AF37]/40 text-[#D4AF37]">
                <Cookie size={20} strokeWidth={1.4} />
              </div>
              <div>
                <p className="label-luxe text-[#D4AF37] mb-2">Confidențialitate</p>
                <p className="text-sm text-[#E5DFC8] leading-relaxed">
                  Folosim cookie-uri esențiale pentru funcționarea site-ului și, opțional, cookie-uri de analiză
                  pentru a înțelege cum interacționezi cu magazinul. Alege ce preferi. Detalii în{" "}
                  <Link to="/legal#privacy" className="text-[#D4AF37] underline underline-offset-4 hover:text-[#F8F4E6]" data-testid="cookie-privacy-link">
                    Politica de Confidențialitate
                  </Link>.
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 lg:shrink-0">
              <button
                onClick={rejectAll}
                className="px-5 py-3 text-xs uppercase tracking-[0.2em] border border-[#A39E8A]/40 text-[#E5DFC8] hover:border-[#E5DFC8] transition"
                data-testid="cookie-reject"
              >
                Respinge
              </button>
              <button
                onClick={() => setShowPrefs(true)}
                className="px-5 py-3 text-xs uppercase tracking-[0.2em] border border-[#D4AF37]/50 text-[#D4AF37] hover:border-[#D4AF37] hover:bg-[#D4AF37]/5 transition"
                data-testid="cookie-preferences"
              >
                Preferințe
              </button>
              <button
                onClick={acceptAll}
                className="px-5 py-3 text-xs uppercase tracking-[0.2em] bg-[#D4AF37] text-[#050505] hover:bg-[#E5C24A] transition font-medium"
                data-testid="cookie-accept-all"
              >
                Acceptă Toate
              </button>
            </div>
          </div>
        ) : (
          <div className="p-6 sm:p-8">
            <div className="flex justify-between items-start mb-6">
              <div>
                <p className="label-luxe text-[#D4AF37] mb-2">Preferințe Cookie-uri</p>
                <p className="text-sm text-[#A39E8A]">Activează categoriile pe care le accepți. Cele esențiale sunt mereu active.</p>
              </div>
              <button onClick={() => setShowPrefs(false)} className="text-[#A39E8A] hover:text-[#D4AF37]" aria-label="Închide preferințele" data-testid="cookie-prefs-close">
                <X size={18} />
              </button>
            </div>

            <div className="space-y-3">
              <PrefRow
                title="Esențiale"
                desc="Necesare pentru autentificare, coș de cumpărături și securitate. Nu pot fi dezactivate."
                checked={true}
                disabled
                onChange={() => {}}
                testid="cookie-cat-essential"
              />
              <PrefRow
                title="Analiză"
                desc="Ne ajută să înțelegem cum este folosit site-ul (PostHog). Anonim, fără date de contact."
                checked={prefs.analytics}
                onChange={(v) => setPrefs(p => ({ ...p, analytics: v }))}
                testid="cookie-cat-analytics"
              />
              <PrefRow
                title="Marketing"
                desc="Folosite pentru personalizarea promoțiilor și campaniilor. În prezent nu activăm astfel de cookie-uri."
                checked={prefs.marketing}
                onChange={(v) => setPrefs(p => ({ ...p, marketing: v }))}
                testid="cookie-cat-marketing"
              />
            </div>

            <div className="flex flex-col sm:flex-row gap-3 mt-6 justify-end">
              <button
                onClick={rejectAll}
                className="px-5 py-3 text-xs uppercase tracking-[0.2em] border border-[#A39E8A]/40 text-[#E5DFC8] hover:border-[#E5DFC8] transition"
                data-testid="cookie-prefs-reject"
              >
                Respinge Toate
              </button>
              <button
                onClick={savePrefs}
                className="px-5 py-3 text-xs uppercase tracking-[0.2em] border border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/5 transition"
                data-testid="cookie-prefs-save"
              >
                Salvează Preferințele
              </button>
              <button
                onClick={acceptAll}
                className="px-5 py-3 text-xs uppercase tracking-[0.2em] bg-[#D4AF37] text-[#050505] hover:bg-[#E5C24A] transition font-medium"
                data-testid="cookie-prefs-accept-all"
              >
                Acceptă Toate
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function PrefRow({ title, desc, checked, disabled, onChange, testid }) {
  return (
    <label className={`flex items-start gap-4 p-4 border border-[#D4AF37]/10 ${disabled ? "opacity-70" : "hover:border-[#D4AF37]/30 cursor-pointer"} transition`}>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => !disabled && onChange(!checked)}
        disabled={disabled}
        className={`relative mt-1 w-10 h-5 shrink-0 transition ${checked ? "bg-[#D4AF37]" : "bg-[#2a2a2a]"} ${disabled ? "cursor-not-allowed" : ""}`}
        data-testid={testid}
      >
        <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-[#050505] transition-transform ${checked ? "translate-x-5" : "translate-x-0"}`} />
      </button>
      <div>
        <p className="text-sm text-[#F8F4E6] font-medium">{title}</p>
        <p className="text-xs text-[#A39E8A] mt-1 leading-relaxed">{desc}</p>
      </div>
    </label>
  );
}
